import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { STORE_INFO } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { submitContact } from '../lib/api';

const ContactPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', reason: 'General Support', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitContact(form);
      toast({ title: 'Message sent!', description: "We'll get back to you within 24 hours." });
      setForm({ name: '', email: '', reason: 'General Support', message: '' });
    } catch {
      toast({ title: 'Error', description: 'Could not send message. Please try again.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">Get In Touch</h1>
        <p className="text-neutral-600 mt-3">Questions about a product or your order? Reach out and our team will be happy to help.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="bg-white border border-neutral-200 rounded-lg p-6 md:p-8">
          <h2 className="text-2xl font-black mb-1">Send Us Feedback</h2>
          <p className="text-sm text-neutral-500 mb-6">Select the reason that best matches your request.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Reason for Contact *</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="General Support">General Support</SelectItem>
                  <SelectItem value="Athlete">Athlete</SelectItem>
                  <SelectItem value="Price Match">Price Match</SelectItem>
                  <SelectItem value="Wholesale">Wholesale</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="message">Message *</Label>
              <Textarea id="message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" />
            </div>
            <Button type="submit" disabled={sending} className="w-full bg-[#0a0a0a] hover:bg-neutral-800 text-white font-bold uppercase tracking-wide">
              {sending ? 'Sending...' : 'Send Your Message'}
            </Button>
          </form>
        </div>

        <div className="space-y-6">
          <div className="bg-[#0a0a0a] text-white rounded-lg p-6 md:p-8">
            <h2 className="text-2xl font-black mb-6">Visit Our Store</h2>
            <div className="space-y-5">
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-[#bfff00] rounded-full flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-black" /></div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-neutral-400">Location</p>
                  <p className="font-semibold">{STORE_INFO.address}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-[#bfff00] rounded-full flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-black" /></div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-neutral-400">Phone</p>
                  <a href={`tel:${STORE_INFO.phoneRaw}`} className="font-semibold hover:text-[#bfff00]">{STORE_INFO.phone}</a>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-[#bfff00] rounded-full flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-black" /></div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-neutral-400">Email</p>
                  <a href={`mailto:${STORE_INFO.email}`} className="font-semibold hover:text-[#bfff00] break-all">{STORE_INFO.email}</a>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-[#bfff00] rounded-full flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-black" /></div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-neutral-400">Hours</p>
                  <p className="font-semibold">{STORE_INFO.hours}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg overflow-hidden border border-neutral-200">
            <iframe
              title="Store map"
              src="https://www.google.com/maps?q=3+Taylor+Court,+Bolton,+ON&output=embed"
              width="100%"
              height="320"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
